import { IFieldInfoProperties, IGetValueArgs, TTemporalDialogSettings } from "@components/smart/FieldInfo";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrEmploymentExtraSalaryEntity,
    IPrEmploymentTemplateExtraSalaryEntity,
    IPrExtraSalaryEntity,
    IPrExtraSalaryLegislativeValueEntity,
    PrBenefitEntity,
    PrEmploymentEntity,
    PrEmploymentExtraSalaryEntity,
    PrEmploymentExtraSalaryTemporalEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemplateExtraSalaryEntity,
    PrEmploymentTemplateExtraSalaryTemporalEntity,
    PrExtraSalaryComputationTypeEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryLegislativeValueEntity,
    PrExtraSalaryTemporalCurrentEntity,
    PrExtraSalaryTemporalEntity,
    PrExtraSalaryTypeEntity,
    PrSalaryComponentEntity,
    TemporalEntityEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrEntityValueSourceCode,
    PrExtraSalaryComputationTypeCode,
    PrExtraSalaryOriginCode
} from "@odata/GeneratedEnums";
import { BatchRequest, isBatchResultOk } from "@odata/OData";
import { IFormatOptions, transformToODataString } from "@odata/OData.utils";
import { ODataQueryResult } from "@odata/ODataParser";
import {
    createNewTemporalLineItem,
    getSyncedLineItemHistoryValues,
    getTemporalDataColumns,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyLineItemFieldDefinition,
    IGetTemporalPropertyLineItemFieldDefinitionArgs,
    TTemporal
} from "@odata/TemporalUtils";
import { TFieldsDefinition } from "@pages/PageUtils";
import { isLegislativeExtraSalary } from "@pages/payroll/employment/Employment.utils";
import { getPayrollCurrencyTableFormatter, getPayrollCurrencyUnit } from "@pages/payroll/payroll.utils";
import i18next from "i18next";

import { BasicInputSizes, FieldType, ValidatorType, ValueType } from "../../../enums";
import { TValue } from "../../../global.types";
import { getUtcDayjs } from "../../../types/Date";
import NumberType from "../../../types/Number";
import { FormStorage } from "../../../views/formView/FormStorage";
import { SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";

export enum ExtraSalaryTab {
    Active = "1",
    Inactive = "2"
}

export function getFilterForTabId(tabId: string): string {
    const nowDate = transformToODataString(getUtcDayjs().startOf("day"), ValueType.Date);
    const DateValidToPath = createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalCurrentEntity.DateValidTo);
    switch (tabId as ExtraSalaryTab) {
        case ExtraSalaryTab.Active:
            return `${DateValidToPath} ge ${nowDate} or ${DateValidToPath} eq null`;
        case ExtraSalaryTab.Inactive:
            return `${DateValidToPath} lt ${nowDate}`;
        case SECONDARY_FILTERS_ALL:
        default:
            return "";
    }
}

export async function createLegislativeExtraSalaries(storage: FormStorage, settings: TTemporalDialogSettings): Promise<(IPrEmploymentTemplateExtraSalaryEntity | IPrEmploymentExtraSalaryEntity)[]> {
    const batch: BatchRequest = storage.oData.batch();
    batch.beginAtomicityGroup("group1");

    // legislative values
    batch.getEntitySetWrapper(EntitySetName.PrExtraSalaryLegislativeValues).query()
            .select(
                    PrExtraSalaryLegislativeValueEntity.ExtraSalaryTypeCode,
                    PrExtraSalaryLegislativeValueEntity.DateValidFrom,
                    PrExtraSalaryLegislativeValueEntity.DateValidTo,
            );
    // preparedExtraSalaries, which are bound to legislative values
    batch.getEntitySetWrapper(EntitySetName.PrExtraSalaries)
            .query()
            .select(PrExtraSalaryEntity.Id, PrExtraSalaryEntity.TypeCode)
            .expand(PrExtraSalaryEntity.Type, (q) => {
                q.select(PrExtraSalaryTypeEntity.OriginCode, PrExtraSalaryTypeEntity.Code, PrExtraSalaryTypeEntity.Name);
            })
            .expand(PrExtraSalaryEntity.CurrentTemporalPropertyBag, (q) => {
                q.expand(PrExtraSalaryTemporalCurrentEntity.ComputationType);
            })
        .filter(`${PrExtraSalaryEntity.Type}/${PrExtraSalaryTypeEntity.OriginCode} eq '${PrExtraSalaryOriginCode.Legislative}'`);

    const results = await batch.execute();

    const templateExtraSalaries: IPrEmploymentTemplateExtraSalaryEntity[] = [];

    if (results.every(res => isBatchResultOk(res))) {
        const legislativeValues = (results[0].body as ODataQueryResult).value as IPrExtraSalaryLegislativeValueEntity[];
        const extraSalaries = (results[1].body as ODataQueryResult).value as IPrExtraSalaryEntity[];

        legislativeValues?.forEach((val, idx) => {
            // Each company can have custom setting of legislative values, configured through extraSalaty entity with
            const matchingSalary = extraSalaries
                    .find(salary => salary.TypeCode === val.ExtraSalaryTypeCode);

            const templateExtraSalaryDefault: IPrEmploymentTemplateExtraSalaryEntity = {
                Default: {
                    Id: matchingSalary.Id,
                    Type: matchingSalary.Type
                },
                // copy current property bag, so there are visible current values on the form
                CurrentTemporalPropertyBag: {
                    ...matchingSalary.CurrentTemporalPropertyBag,
                    NameSourceCode: PrEntityValueSourceCode.Default,
                    ComputationTypeSourceCode: PrEntityValueSourceCode.Default,
                    ValueSourceCode: PrEntityValueSourceCode.Default,
                },
            };
            const defaultRange: TTemporal = {
                DateValidFrom: val.DateValidFrom,
                DateValidTo: val.DateValidTo
            };
            const templateExtraSalary = createNewTemporalLineItem(settings, templateExtraSalaryDefault, defaultRange);

            templateExtraSalaries.push(templateExtraSalary);
        });
    }

    return templateExtraSalaries;
}

export const getExtraSalaryValueUnit = (args: IGetValueArgs): string => {
    const parentBc = args.bindingContext.getParent();
    const typeBc = parentBc.navigate(PrExtraSalaryTemporalEntity.ComputationType);
    const type = args.storage.getValue(typeBc);
    return type?.Code === PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage ? "%" : getPayrollCurrencyUnit()(args);
};

export const extraSalaryValueFormatter = (val: TValue, args: IFormatOptions): string => {
    if (args.entity?.CurrentTemporalPropertyBag?.ComputationTypeCode === PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage) {
        return `${NumberType.format(val as number, { maximumFractionDigits: 0 })} %`;
    }
    return getPayrollCurrencyTableFormatter()(val, args);
};

export const extraSalariesTypeFormatter = (val: TValue): string => {
    const typeCode = val as PrExtraSalaryOriginCode;
    return i18next.t(`ExtraSalary:Type.` + typeCode).toString();
};

function getLineItemBindingContext(bc: BindingContext, collectionName: string): BindingContext {
    while (bc?.getPath(true) !== collectionName) {
        bc = bc.getParent();
    }
    return bc;
}

export function isPartOfLegislativeExtraSalaryItem({ storage, bindingContext }: IGetValueArgs): boolean {
    if (!bindingContext) {
        // todo: keep here for now, as it is not clear where this is called from with undefined bindingContext
        console.trace("isPartOfLegislativeExtraSalaryItem: bindingContext is undefined");
        return false;
    }
    const isExtraSalaryEntity = storage.data.bindingContext.getPath(true) === EntitySetName.PrExtraSalaries;
    const bc = isExtraSalaryEntity ? storage.data.bindingContext
            : getLineItemBindingContext(bindingContext, PrEmploymentTemplateEntity.ExtraSalaries);
    return isLegislativeExtraSalary({ storage, bindingContext: bc });
}

export function getExtraSalaryFieldDefinition(propName: string, temporalDialogSettings?: TTemporalDialogSettings): Partial<IFieldInfoProperties> {
    let def: Partial<IFieldInfoProperties> = {};
    switch(propName) {
        case FakeTypePath:
            def = {
                label: i18next.t("ExtraSalary:Form.Type"),
                isReadOnly: true,
                customizationData: {
                    useForCustomization: false
                },
                formatter: (val: TValue, args: IFormatOptions) => {
                    const value = args.storage.getValueByPath(createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode));
                    return extraSalariesTypeFormatter(value as string);
                }
            };
            break;
        case PrExtraSalaryTemporalEntity.Name:
            def = {
                width: BasicInputSizes.L,
                isReadOnly: isPartOfLegislativeExtraSalaryItem,
                isRequired: true
            };
            break;
        case PrExtraSalaryTemporalEntity.ComputationType:
            def = {
                type: FieldType.ComboBox,
                width: BasicInputSizes.XL,
                defaultValue: PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage,
                fieldSettings: {
                    displayName: PrExtraSalaryComputationTypeEntity.Name
                },
                affectedFields: [{
                    id: PrExtraSalaryTemporalCurrentEntity.Value,
                    navigateFromParent: true
                }],
                isReadOnly: isPartOfLegislativeExtraSalaryItem
            };
            break;
        case createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin):
            def = {
                width: BasicInputSizes.S,
                defaultValue: PrExtraSalaryOriginCode.Custom,
                type: FieldType.ComboBox,
                isReadOnly: true,
                formatter: extraSalariesTypeFormatter
            };
            break;
        case PrExtraSalaryTemporalEntity.Value:
            def = {
                type: FieldType.NumberInput,
                width: BasicInputSizes.S,
                isRequired: true,
                fieldSettings: {
                    unit: getExtraSalaryValueUnit,
                    min: 0
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        excludeMin: true
                    }
                },
                // isRequired: isExtraSalaryWithoutDefault
            };
            break;
    }

    return temporalDialogSettings ? {
        ...def,
        fieldSettings: {
            ...def.fieldSettings ?? {},
            temporalDialog: temporalDialogSettings
        }
    } : def;
}

// we want to show Type value in SmartTemporalPropertyDialog,
// even though it is not a temporal value => use local context
export const FakeTypePath = BindingContext.localContext("Type");

export enum ExtraSalaryVariant {
    Base = "Base",
    Employment = "Employment",
    EmploymentTemplate = "EmploymentTemplate"
}

export function getExtraSalaryColumns(type: ExtraSalaryVariant = ExtraSalaryVariant.Base): string[] {
    const ExtraSalaryEntity = type === ExtraSalaryVariant.Employment ? PrEmploymentExtraSalaryTemporalEntity
            : type === ExtraSalaryVariant.EmploymentTemplate ? PrEmploymentTemplateExtraSalaryTemporalEntity : PrExtraSalaryTemporalEntity;

    return [
        ExtraSalaryEntity.Name,
        FakeTypePath,
        ExtraSalaryEntity.ComputationType,
        ExtraSalaryEntity.Value,
    ].filter(item => item !== FakeTypePath || type === ExtraSalaryVariant.Base);
}

// needs to be in callback, so that translation is loaded
export const getExtraSalaryTemporalDialogSettings = (type: ExtraSalaryVariant = ExtraSalaryVariant.Base, withHistory = true): TTemporalDialogSettings => {
    return {
        dialogTitle: i18next.t("ExtraSalary:Form.PlanExtraSalaryTitle"),
        columns: getExtraSalaryColumns(type),
        withoutGaps: isPartOfLegislativeExtraSalaryItem,
        extractHistory: withHistory ? getSyncedLineItemHistoryValues : undefined,
        granularity: "month"
    };
};

export function getExtraSalaryFieldDefinitions(type: ExtraSalaryVariant.Employment | ExtraSalaryVariant.EmploymentTemplate = ExtraSalaryVariant.Employment): TFieldsDefinition {
    const isEmployment = type === ExtraSalaryVariant.Employment;

    const MainEntity = isEmployment ? PrEmploymentEntity : PrEmploymentTemplateEntity;
    const ExtraSalaryEntity = isEmployment ? PrEmploymentExtraSalaryEntity : PrEmploymentTemplateExtraSalaryEntity;
    const ExtraSalaryTemporalEntity = isEmployment ? PrEmploymentExtraSalaryTemporalEntity : PrEmploymentTemplateExtraSalaryTemporalEntity;

    const temporalDialogSettings: TTemporalDialogSettings = getExtraSalaryTemporalDialogSettings(type, true);

    const commonDefs: Omit<IGetTemporalPropertyLineItemFieldDefinitionArgs, "propName"> = {
        collectionName: MainEntity.ExtraSalaries,
        withOpeningButton: false,
        isSynchronized: true
    };

    const columns = getExtraSalaryColumns(type);
    const dataColumns = getTemporalDataColumns(columns);

    // additional properties, which are attached to the amount field
    const additionalProperties = dataColumns
            .map(id => ({ id: `/${MainEntity.ExtraSalaries}/${PrBenefitEntity.TemporalPropertyBag}/${id}` }));

    [TemporalEntityEntity.DateValidFrom, TemporalEntityEntity.DateValidTo].forEach(id => {
        additionalProperties.push({ id: `/${createPath(MainEntity.ExtraSalaries, PrSalaryComponentEntity.CurrentTemporalPropertyBag, id)}`});
    });

    dataColumns.forEach(id => {
        additionalProperties.push({ id: `/${createPath(MainEntity.ExtraSalaries, ExtraSalaryEntity.Default, PrSalaryComponentEntity.CurrentTemporalPropertyBag, id)}`});
        additionalProperties.push({ id: `/${createPath(MainEntity.ExtraSalaries, ExtraSalaryEntity.Default, PrSalaryComponentEntity.TemporalPropertyBag, id)}`});
    });

    // const isExistingLineItem = (args: IGetValueArgs) => {
    //     return !args.bindingContext.getParent().isNew();
    // };

    return {
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: ExtraSalaryTemporalEntity.Name,
            definition: {
                ...getExtraSalaryFieldDefinition(ExtraSalaryTemporalEntity.Name, temporalDialogSettings)
            },
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: ExtraSalaryTemporalEntity.ComputationType,
            definition: {
                ...getExtraSalaryFieldDefinition(ExtraSalaryTemporalEntity.ComputationType, temporalDialogSettings)
            },
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: ExtraSalaryTemporalEntity.Value,
            definition: {
                ...getExtraSalaryFieldDefinition(ExtraSalaryTemporalEntity.Value, temporalDialogSettings),
                additionalProperties
            },
            currentDefinition: {
                extraFieldContentAfter: (args) => isPartOfLegislativeExtraSalaryItem(args) ? null : getTemporalPropertyExtraFieldContentAfter()(args)
            }
        }),
    };
}
